import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';

/**
 * Extracts a user-facing error message from an Axios error, preferring the
 * API's own error body (response.data.message) over the generic
 * "Request failed with status code 400".
 */
function errMessage(err: unknown, fallback: string): string {
  const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
  return anyErr?.response?.data?.message ?? anyErr?.message ?? fallback;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PersonSummary {
  personName: string;
  faceCount: number;
}

export interface FaceEntry {
  faceId: string;
  mediaId: string;
  indexedAt: string;
  boundingBox: { top: number; left: number; width: number; height: number };
}

export interface PersonDetail {
  personName: string;
  faces: FaceEntry[];
}

export interface IndexFaceRequest {
  mediaId: string;
  personName: string;
  boundingBox: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}

export interface IndexFaceResponse {
  faceId: string;
  personName: string;
  mediaId: string;
  boundingBox: { top: number; left: number; width: number; height: number };
}

export interface FaceCollectionState {
  persons: PersonSummary[];
  selectedPerson: PersonDetail | null;
  loading: boolean;
  error: string | null;
  indexing: boolean;
  indexError: string | null;
}

const initialState: FaceCollectionState = {
  persons: [],
  selectedPerson: null,
  loading: false,
  error: null,
  indexing: false,
  indexError: null,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Fetch all indexed persons with face counts.
 * GET /faces
 * Requirements: 5.1, 5.2
 */
export const fetchPersons = createAsyncThunk(
  'faceCollection/fetchPersons',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<{ persons: PersonSummary[] }>('/faces');
      return response.data.persons;
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to fetch persons'));
    }
  },
);

/**
 * Fetch detail for a specific person (face entries with source media).
 * GET /faces/{personName}
 * Requirements: 5.1
 */
export const fetchPersonDetail = createAsyncThunk(
  'faceCollection/fetchPersonDetail',
  async (personName: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<PersonDetail>(
        `/faces/${encodeURIComponent(personName)}`,
      );
      return response.data;
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to fetch person detail'));
    }
  },
);

/**
 * Index a face from a media image into the face collection.
 * POST /faces/index
 * Requirements: 1.2
 */
export const indexFace = createAsyncThunk(
  'faceCollection/indexFace',
  async (request: IndexFaceRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<IndexFaceResponse>('/faces/index', request);
      return response.data;
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to index face'));
    }
  },
);

/**
 * Remove all faces for a person from the collection.
 * DELETE /faces/{personName}
 * Requirements: 5.3
 */
export const removePerson = createAsyncThunk(
  'faceCollection/removePerson',
  async (personName: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/faces/${encodeURIComponent(personName)}`);
      return personName;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove person';
      return rejectWithValue(message);
    }
  },
);

/**
 * Remove a single face entry for a person.
 * DELETE /faces/{personName}/{faceId}
 * Requirements: 5.5
 */
export const removeFace = createAsyncThunk(
  'faceCollection/removeFace',
  async ({ personName, faceId }: { personName: string; faceId: string }, { rejectWithValue }) => {
    try {
      await apiClient.delete(
        `/faces/${encodeURIComponent(personName)}/${encodeURIComponent(faceId)}`,
      );
      return { personName, faceId };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove face';
      return rejectWithValue(message);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const faceCollectionSlice = createSlice({
  name: 'faceCollection',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearIndexError(state) {
      state.indexError = null;
    },
    clearSelectedPerson(state) {
      state.selectedPerson = null;
    },
  },
  extraReducers: (builder) => {
    // fetchPersons
    builder
      .addCase(fetchPersons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPersons.fulfilled, (state, action) => {
        state.loading = false;
        state.persons = action.payload;
      })
      .addCase(fetchPersons.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to fetch persons';
      });

    // fetchPersonDetail
    builder
      .addCase(fetchPersonDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPersonDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPerson = action.payload;
      })
      .addCase(fetchPersonDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to fetch person detail';
      });

    // indexFace
    builder
      .addCase(indexFace.pending, (state) => {
        state.indexing = true;
        state.indexError = null;
      })
      .addCase(indexFace.fulfilled, (state) => {
        state.indexing = false;
        state.indexError = null;
      })
      .addCase(indexFace.rejected, (state, action) => {
        state.indexing = false;
        state.indexError = (action.payload as string) ?? 'Failed to index face';
      });

    // removePerson
    builder
      .addCase(removePerson.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removePerson.fulfilled, (state, action) => {
        state.loading = false;
        state.persons = state.persons.filter((p) => p.personName !== action.payload);
        if (state.selectedPerson?.personName === action.payload) {
          state.selectedPerson = null;
        }
      })
      .addCase(removePerson.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to remove person';
      });

    // removeFace
    builder
      .addCase(removeFace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFace.fulfilled, (state, action) => {
        state.loading = false;
        const { personName, faceId } = action.payload;
        // Update the selected person detail if it matches
        if (state.selectedPerson?.personName === personName) {
          state.selectedPerson.faces = state.selectedPerson.faces.filter(
            (f) => f.faceId !== faceId,
          );
        }
        // Decrement the face count in the persons list
        const person = state.persons.find((p) => p.personName === personName);
        if (person) {
          person.faceCount -= 1;
          if (person.faceCount <= 0) {
            state.persons = state.persons.filter((p) => p.personName !== personName);
          }
        }
      })
      .addCase(removeFace.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to remove face';
      });
  },
});

export const { clearError, clearIndexError, clearSelectedPerson } = faceCollectionSlice.actions;

// Selectors
export const selectPersons = (state: { faceCollection: FaceCollectionState }) =>
  state.faceCollection.persons;
export const selectSelectedPerson = (state: { faceCollection: FaceCollectionState }) =>
  state.faceCollection.selectedPerson;
export const selectFaceCollectionLoading = (state: { faceCollection: FaceCollectionState }) =>
  state.faceCollection.loading;
export const selectFaceCollectionError = (state: { faceCollection: FaceCollectionState }) =>
  state.faceCollection.error;
export const selectIndexing = (state: { faceCollection: FaceCollectionState }) =>
  state.faceCollection.indexing;
export const selectIndexError = (state: { faceCollection: FaceCollectionState }) =>
  state.faceCollection.indexError;

export default faceCollectionSlice.reducer;
